import { useQuery } from '@tanstack/react-query';
import { announcementApi, type AnnouncementOverviewResponse } from '../api';
import type { AnnouncementFiltersType } from '../components/AnnouncementFilters';
import type { ResidentAnnouncementDetails } from '../types';
// import { showApiError } from '../../../shared/utils/apiErrorHandler'

export const useAnnouncementQueries = () => {
  // Get all announcements
  // const useAnnouncements = () => {
  //   return useQuery({
  //     queryKey: ['announcements'],
  //     queryFn: announcementApi.getAnnouncements,
  //     staleTime: 5 * 60 * 1000, // 5 minutes
  //   });
  // };

  // Get announcement by ID
  const useAnnouncement = (id: string) => {
    return useQuery({
      queryKey: ['announcements', id],
      queryFn: () => announcementApi.getAnnouncementById(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get announcements by house
  const useAnnouncementsByHouse = (houseId: string) => {
    return useQuery({
      queryKey: ['announcements', 'house', houseId],
      queryFn: () => announcementApi.getAnnouncementsByHouse(houseId),
      enabled: !!houseId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get announcements by priority
  const useAnnouncementsByPriority = (priority: string) => {
    return useQuery({
      queryKey: ['announcements', 'priority', priority],
      queryFn: () => announcementApi.getAnnouncementsByPriority(priority),
      enabled: !!priority,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  return {
    useAnnouncements,
    useAnnouncement,
    useAnnouncementsByHouse,
    useAnnouncementsByPriority,
  };
};

// Export individual hooks for convenience with error handling
// export const useAnnouncements = () => {
//   return useQuery({
//     queryKey: ['announcements'],
//     queryFn: announcementApi.getAnnouncements,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     retry: (failureCount, error: any) => {
//       if (error?.response?.status >= 400 && error?.response?.status < 500) {
//         if (![408, 429].includes(error.response.status)) {
//           return false;
//         }
//       }
//       return failureCount < 3;
//     },
//     retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
//     // onError removed - use error handling in components instead
//   });
// };

export const useAnnouncement = (id: string) => {
  return useQuery({
    queryKey: ['announcements', id],
    queryFn: () => announcementApi.getAnnouncementById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        if (![408, 429].includes(error.response.status)) {
          return false;
        }
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // onError removed - use error handling in components instead
  });
};

export const useResidentAnnouncements = (filters: AnnouncementFiltersType) => {
  return useQuery({
    queryKey: ['residentAnnouncements', filters],
    queryFn: () => announcementApi.getUserAnnouncements(filters),
  });
};

export const useAnnouncements = (filters: AnnouncementFiltersType) => {
  return useQuery({
    queryKey: ['announcements', filters],
    queryFn: () => announcementApi.getAnnouncements(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors except 408, 429
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        if (![408, 429].includes(error.response.status)) {
          return false;
        }
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    placeholderData: (prev: any) => prev,
    // onError removed - use error handling in components instead
  });
};

export const useResidentAnnouncementDetails = (id: string) => {
  return useQuery<ResidentAnnouncementDetails>({
    queryKey: ['residentAnnouncementDetails', id],
    queryFn: () => announcementApi.getAnnouncementById(id),
    enabled: !!id, // ✅ avoids firing before the ID exists
  });
};

export const useAnnouncementOverview = (timeRange = 30) => {
  return useQuery<AnnouncementOverviewResponse>({
    queryKey: ['announcement-overview', timeRange],
    queryFn: () => announcementApi.getOverview(timeRange),
  });
};
